import { claimChainOfferStep, claimFreeOffer, getOffersConditional, validateOfferPurchase } from '../api/requests/offers.requests.js';
import { StatisticsStorage } from '../storage/statistics_storage.js';
import type { RewardedShowResult } from '../ads/rewarded.js';
import { OfferEventType, OfferStore } from '../api/dto/offers.dto.js';
import type { ClaimChainOfferStepResponse, ClaimFreeOfferResponse, EntitlementDto, OfferDto, OfferProductDto, OfferProductsDto, OffersResponse, ResolvedOfferPlacementDto, ValidatePurchaseRequest, ValidatePurchaseResponse } from '../api/dto/offers.dto.js';
import { IapPlugin } from '../plugins/iap_plugin.js';
import {
  buildOfferPriceAnalyticsProperties,
  trackOfferAnalytics,
} from './offer_analytics.js';
import {
  isPurchaseNetworkError,
  markPurchaseErrorReported,
  markPurchaseStage,
  purchaseFailureProperties,
  purchaseStageOf,
  shouldCapturePurchaseFailure,
  type PurchaseFailureStage,
} from './purchase_failure.js';
import { ApiError } from '../api/http_client.js';
import { crashCapture } from '../crash/crash_reporter.js';
import { CrashBreadcrumbs } from '../crash/breadcrumbs.js';
import { GlobalSettings, RuntimeEnvironmentKey } from '../global_settings.js';
import { GIT_BRANCH } from '../generated/git_branch.js';
import { CustomEventsController } from '../socket_handler/controllers/custom_events_controller.js';
import { logRewardCoins, rewardsLabel, trackFeatureEvent } from '../analytics/feature_events.js';

const NO_ADS_ENTITLEMENT = 'no_ads';
const LEGACY_OFFERS_CACHE_KEY = 'eropix_offers_cache';
const PERSISTENT_ENTITLEMENTS_KEY = 'royal_smash_offer_entitlements';

export type MonetizationSegment = 'free' | 'paid';

export type ChainLayout = 'chain_default' | 'chain_video';

export function chainLayoutOf( offer: OfferDto ): ChainLayout
{
  return offer.visual === 'chain_video' ? 'chain_video' : 'chain_default';
}

let offers_cache: OfferDto[] = [];
let placements_cache: Record<string, ResolvedOfferPlacementDto> = {};
let entitlements_cache: Record<string, boolean> = {};
let refresh_promise: Promise<OffersResponse | null> | null = null;
let offers_etag: string | null = null;
let last_offers_response: OffersResponse | null = null;
let server_clock_offset_ms: number | null = null;
let schedule_change_at_ms: number | null = null;
let schedule_refresh_timer: ReturnType<typeof setTimeout> | null = null;
const SCHEDULE_REFRESH_MIN_DELAY_MS = 5_000;
const SCHEDULE_REFRESH_MAX_DELAY_MS = 2_147_483_647;
// Compatibility fallback for a backend that has the duration field but has
// not yet been updated to return the corresponding server deadline. It stays
// memory-only, so it has the same lifetime as a client session and refreshes
// cannot extend it.
const session_offer_display_deadlines_ms = new Map<string, number>();
// Intentionally memory-only: a new app/browser session gets a fresh server
// window, while refreshes inside it cannot reset a timed offer.
const offer_session_id = typeof globalThis.crypto?.randomUUID === 'function'
  ? globalThis.crypto.randomUUID()
  : `offer-${Date.now()}-${Math.random().toString( 36 ).slice( 2 )}`;
// Debug-only runtime override. It intentionally is neither persisted nor sent to the API/analytics.
let no_ads_cheat_override: boolean | null = null;
// Unlike ENV, this is generated at build time and cannot be changed by a
// runtime localStorage override. A production binary never gets this cheat.
const PURCHASE_SIMULATION_BUILD_ALLOWED = ![ RuntimeEnvironmentKey.PROD, 'production' ].includes( String( GIT_BRANCH ) );
// Debug-only and memory-only. A simulated purchase is still validated and
// granted by the server, but never opens the native store and is persisted as
// a sandbox/test purchase. Production builds cannot enable it.
let simulate_purchase_cheat_enabled = false;
const SIMULATED_PURCHASE_CURRENCY = 'EUR';
const SIMULATED_PURCHASE_PRICE_MICROS = 4_490_000;
// Retained until the server confirms the result, so a lost HTTP response
// retries the same idempotent free claim instead of issuing a second claim.
const pending_free_claim_ids = new Map<string, string>();

function createSimulatedPurchase( product_id: string ): ValidatePurchaseRequest
{
  const transaction_id = typeof globalThis.crypto?.randomUUID === 'function'
    ? globalThis.crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString( 36 ).slice( 2 )}`;

  return {
    store: OfferStore.Sandbox,
    product_id,
    transaction_id: `cheat-test-${transaction_id}`,
    environment: 'sandbox',
    currency: SIMULATED_PURCHASE_CURRENCY,
    price_micros: SIMULATED_PURCHASE_PRICE_MICROS,
    ...( IapPlugin.getStore() === OfferStore.GooglePlay ? { sandbox_platform: 'android' as const } : {} ),
    ...( IapPlugin.getStore() === OfferStore.AppStore ? { sandbox_platform: 'ios' as const } : {} ),
  };
}

function isEntitlementActive( entitlement: boolean | EntitlementDto | undefined ): boolean
{
  if ( entitlement === true ) return true;
  if ( entitlement && typeof entitlement === 'object' ) return entitlement.active === true;
  return false;
}

function readPositiveDurationSeconds( value: unknown ): number | null
{
  const duration = typeof value === 'number' ? value : Number( value );
  return Number.isInteger( duration ) && duration > 0 ? duration : null;
}

function getOfferDurationFallbackSeconds( offer: OfferDto ): number | null
{
  const durations = [
    offer.shown_duration_seconds,
    offer.first_shown_duration_seconds,
    offer.data?.shown_duration_seconds,
    offer.data?.shownDurationSeconds,
    offer.data?.first_shown_duration_seconds,
    offer.data?.firstShownDurationSeconds,
  ]
    .map( readPositiveDurationSeconds )
    .filter( ( duration ): duration is number => duration !== null );
  return durations.length ? Math.min( ...durations ) : null;
}

function normalizeEntitlements( entitlements: OffersResponse['entitlements'] | null | undefined ): Record<string, boolean>
{
  const normalized: Record<string, boolean> = {};
  if ( !entitlements ) return normalized;

  for ( const [ key, entitlement ] of Object.entries( entitlements ) )
    normalized[key] = isEntitlementActive( entitlement );

  return normalized;
}

function readPersistentEntitlements(): Record<string, boolean>
{
  try
  {
    const raw = localStorage.getItem( PERSISTENT_ENTITLEMENTS_KEY );
    if ( !raw ) return {};

    const parsed = JSON.parse( raw ) as Record<string, unknown>;
    const entitlements: Record<string, boolean> = {};
    for ( const [ key, value ] of Object.entries( parsed ) )
    {
      if ( value === true )
        entitlements[key] = true;
    }

    return entitlements;
  }
  catch
  {
    return {};
  }
}

function persistEntitlement( key: string ): void
{
  try
  {
    localStorage.setItem( PERSISTENT_ENTITLEMENTS_KEY, JSON.stringify( {
      ...readPersistentEntitlements(),
      [key]: true,
    } ) );
  }
  catch {}
}

function clearPersistentEntitlements(): void
{
  try { localStorage.removeItem( PERSISTENT_ENTITLEMENTS_KEY ); }
  catch {}
}

function clearLegacyPersistentCache(): void
{
  try { localStorage.removeItem( LEGACY_OFFERS_CACHE_KEY ); }
  catch {}
}

function getMonetizationSegment(): MonetizationSegment
{
  return Object.values( entitlements_cache ).some( active => active === true ) ? 'paid' : 'free';
}

function emitEntitlementsChanged( include_monetization_segment: boolean = true ): void
{
  try
  {
    window.dispatchEvent( new CustomEvent( 'offersEntitlementsChanged', {
      detail: {
        entitlements: { ...entitlements_cache },
        ...( include_monetization_segment ? { monetization_segment: getMonetizationSegment() } : {} ),
      },
    } ) );
  }
  catch {}
}

function emitPlacementsChanged(): void
{
  try { window.dispatchEvent( new CustomEvent( 'offersPlacementsChanged' ) ); }
  catch {}
}

/**
 * A purchase/claim response carries the offer's new cycle state so the shop
 * can redraw the card without another catalog round trip.
 */
function purchaseLimitAnalytics( result: { purchases_left?: number; limit_resets_at?: string } ): Record<string, unknown>
{
  return {
    ...( result.purchases_left !== undefined ? { purchases_left: result.purchases_left } : {} ),
    ...( result.limit_resets_at ? { limit_resets_at: result.limit_resets_at } : {} ),
  };
}

function applyPurchaseLimitResult( offer_id: string, result: { purchases_left?: number; limit_resets_at?: string } ): void
{
  if ( result.purchases_left === undefined && result.limit_resets_at === undefined ) return;
  offers_cache = offers_cache.map( item => item.id === offer_id
    ? { ...item, purchases_left: result.purchases_left, limit_resets_at: result.limit_resets_at }
    : item );
}

function applyOffersResponse( response: OffersResponse ): void
{
  const server_time_ms = typeof response.server_time === 'string' ? Date.parse( response.server_time ) : NaN;
  if ( Number.isFinite( server_time_ms ) ) server_clock_offset_ms = server_time_ms - Date.now();
  offers_cache = Array.isArray( response.offers ) ? response.offers : [];
  placements_cache = Object.fromEntries( Object.entries( response.placements ?? {} ).filter( ( [ id, placement ] ) =>
    Boolean( id ) && Boolean( placement?.offer_id ) && Boolean( placement?.template )
  ) );
  entitlements_cache = {
    ...normalizeEntitlements( response.entitlements ),
    ...readPersistentEntitlements(),
  };

  for ( const offer of offers_cache )
  {
    const has_server_deadline = [
      offer.session_ends_at,
      offer.first_shown_ends_at,
      offer.ends_at,
      offer.data?.session_ends_at,
      offer.data?.first_shown_ends_at,
      offer.data?.ends_at,
      offer.data?.endsAt,
    ].some( value => typeof value === 'string' && Number.isFinite( Date.parse( value ) ) );
    const duration_fallback_seconds = getOfferDurationFallbackSeconds( offer );
    if ( !has_server_deadline && duration_fallback_seconds !== null
      && !session_offer_display_deadlines_ms.has( offer.id ) )
    {
      session_offer_display_deadlines_ms.set( offer.id, Date.now() + duration_fallback_seconds * 1000 );
    }

    if ( offer.owned )
      entitlements_cache[offer.entitlement_key] = true;
  }

  emitEntitlementsChanged();
  emitPlacementsChanged();
  scheduleRefreshAtNextChange( response );
}

function scheduleRefreshAtNextChange( response: OffersResponse ): void
{
  if ( schedule_refresh_timer !== null ) clearTimeout( schedule_refresh_timer );
  schedule_refresh_timer = null;
  const candidates = [ response.next_change_at, ...offers_cache.map( offer => offer.cycle ? offer.ends_at : undefined ) ]
    .filter( ( value ): value is string => typeof value === 'string' )
    .map( value => Date.parse( value ) )
    .filter( Number.isFinite );
  schedule_change_at_ms = candidates.length ? Math.min( ...candidates ) : null;
  if ( schedule_change_at_ms === null ) return;
  const server_now_ms = Date.now() + ( server_clock_offset_ms ?? 0 );
  const delay_ms = Math.min( SCHEDULE_REFRESH_MAX_DELAY_MS, Math.max( SCHEDULE_REFRESH_MIN_DELAY_MS, schedule_change_at_ms - server_now_ms + 1_000 ) );
  schedule_refresh_timer = setTimeout( () =>
  {
    schedule_refresh_timer = null;
    void OfferService.refresh();
  }, delay_ms );
}

export function getPlatformProductConfig( products: OfferProductsDto ): OfferProductDto | null
{
  const store = IapPlugin.getStore();
  if ( store === OfferStore.GooglePlay ) return products.android ?? null;
  if ( store === OfferStore.AppStore ) return products.ios ?? null;
  return products.android ?? products.ios ?? null;
}

function getPlatformProduct( offer: OfferDto ): string | null
{
  return getPlatformProductConfig( offer.products )?.productId ?? null;
}

type RestoredOfferMatch = {
  offer: OfferDto;
  product: OfferProductDto;
  chain_step_id?: string;
};

/** A chain owns its store SKUs on individual cards, not on the parent offer. */
function findRestoredOfferMatch( product_id: string ): RestoredOfferMatch | null
{
  for ( const offer of offers_cache )
  {
    const product = Object.values( offer.products ).find( item => item?.productId === product_id );
    if ( product ) return { offer, product };

    const matching_steps = offer.chain?.steps.filter( item =>
      Object.values( item.products ?? {} ).some( product => product?.productId === product_id )
    ) ?? [];
    // A chain is allowed to reuse an SKU. During restore, the server-issued
    // cursor identifies which matching card is awaiting validation.
    const current_step_id = offer.chain?.next_step_id ?? offer.chain?.next_paid_step_id;
    const step = matching_steps.find( item => item.id === current_step_id ) ?? matching_steps[0];
    const chain_product = step && Object.values( step.products ?? {} ).find( product => product?.productId === product_id );
    if ( step && chain_product ) return { offer, product: chain_product, chain_step_id: step.id };
  }
  return null;
}

function isFreeOffer( offer: OfferDto ): boolean
{
  return offer.purchase?.type === 'free';
}

function isRewardedOffer( offer: OfferDto ): boolean
{
  return offer.purchase?.type === 'rewarded';
}

function getPlatformPurchaseMetadata( offer: OfferDto, product_override?: OfferProductDto ): Partial<ValidatePurchaseRequest>
{
  const product = product_override ?? getPlatformProductConfig( offer.products );

  return {
    ...( product?.packageName ? { package_name: product.packageName } : {} ),
    ...( product?.bundleId ? { bundle_id: product.bundleId } : {} ),
    ...( product?.environment ? { environment: product.environment } : {} ),
  };
}

/** Where a chain step is acted on: the chain screen by default, or a placement such as the 1+1 lobby offer. */
export interface ChainStepContext
{
  placement_id?: string;
  placement?: string;
  template?: string;
}

function grantedAnalytics( granted: Record<string, unknown> | null | undefined ): Record<string, unknown>
{
  const label = rewardsLabel( granted );
  return label ? { granted_rewards: label } : {};
}

function rewardSource( offer: OfferDto, kind: 'free' | 'purchase' | 'chain_free' | 'chain_video' ): string
{
  if ( offer.placement?.template === 'mystery_box' ) return 'mystery_box';
  if ( offer.chain ) return kind === 'chain_video' ? 'chain_offer_video' : kind === 'purchase' ? 'chain_offer_purchase' : 'chain_offer_free';
  return kind === 'free' ? 'offer_free' : 'offer_purchase';
}

/** A free or rewarded chain card: free_claim with what was granted, or purchase_failed when the claim fails. */
async function trackChainStepClaim( offer: OfferDto, step_context: Record<string, unknown>, claim: () => Promise<ClaimChainOfferStepResponse> ): Promise<ClaimChainOfferStepResponse>
{
  try
  {
    const result = await claim();
    await trackOfferAnalytics( offer, OfferEventType.FreeClaim, { ...step_context, grant_applied: result.grant_applied, ...grantedAnalytics( result.granted ) } );
    if ( result.grant_applied !== false ) logRewardCoins( result.granted, rewardSource( offer, step_context.rewarded ? 'chain_video' : 'chain_free' ) );
    return result;
  }
  catch ( error )
  {
    await reportPurchaseFailure( offer, step_context, error, 'claim' );
    throw error;
  }
}

function chainStepAnalytics( context: ChainStepContext | undefined, step_id: string ): Record<string, unknown>
{
  return {
    placement: context?.placement ?? context?.placement_id ?? 'chain_offer',
    ...( context?.placement_id ? { placement_id: context.placement_id } : {} ),
    ...( context?.template ? { template: context.template } : {} ),
    chain_step_id: step_id,
  };
}

function withOfferPurchaseMetadata( offer: OfferDto, purchase: ValidatePurchaseRequest, product_override?: OfferProductDto, chain_step_id?: string, trigger_event_id?: string, placement_id?: string ): ValidatePurchaseRequest
{
  return {
    session_id: offer_session_id,
    ...getPlatformPurchaseMetadata( offer, product_override ),
    ...( chain_step_id ? { chain_step_id } : {} ),
    ...( trigger_event_id ? { trigger_event_id } : {} ),
    ...( placement_id ? { placement_id } : {} ),
    ...purchase,
  };
}

const PURCHASE_CRASH_KEYS = [
  'productid', 'placement', 'placement_id', 'chain_step_id', 'free', 'rewarded', 'restored',
  'purchase_stage', 'failure_reason', 'failure_code', 'billing_response', 'http_status',
  'store', 'transaction_id', 'purchase_duration_ms', 'initial_failure_reason', 'recovered_owned_product',
];

function purchaseCrashContext( offer_id: string | undefined, properties: Record<string, unknown> ): Record<string, unknown>
{
  const context: Record<string, unknown> = offer_id ? { offer_id } : {};
  for ( const key of PURCHASE_CRASH_KEYS )
    if ( properties[key] !== undefined ) context[key] = properties[key];
  return context;
}

async function reportPurchaseFailure(
  offer: OfferDto,
  context: Record<string, unknown>,
  error: unknown,
  stage: PurchaseFailureStage,
  options: { capture?: boolean; extra?: Record<string, unknown>; crash_extra?: Record<string, unknown> } = {},
): Promise<void>
{
  if ( stage === 'validation' ) markPurchaseStage( error, 'validation' );
  const properties = { ...context, ...purchaseFailureProperties( error, stage ), ...options.extra };
  CrashBreadcrumbs.add( 'app', `purchase failed offer=${offer.id} stage=${stage} reason=${String( properties.failure_reason )}` );
  if ( options.capture ?? shouldCapturePurchaseFailure( error, stage ) )
    crashCapture( error, { ...purchaseCrashContext( offer.id, properties ), ...options.crash_extra } );
  markPurchaseErrorReported( error );
  await trackOfferAnalytics( offer, OfferEventType.PurchaseFailed, properties );
}

function reportOfferUnavailable( offer_id: string, error: Error, context?: Record<string, unknown> ): Error
{
  const properties: Record<string, unknown> = { offer_id, ...context, ...purchaseFailureProperties( error, 'setup' ) };
  CrashBreadcrumbs.add( 'app', `purchase blocked offer=${offer_id} reason=${String( properties.failure_reason )}` );
  trackFeatureEvent( 'offer_purchase_unavailable', properties );
  crashCapture( error, purchaseCrashContext( offer_id, properties ) );
  markPurchaseErrorReported( error );
  return error;
}

function reportRestoreQueryFailure( error: unknown ): void
{
  const failure = purchaseFailureProperties( error, 'restore' );
  const store = IapPlugin.getStore();
  CrashBreadcrumbs.add( 'app', `purchase restore failed reason=${String( failure.failure_reason )}` );
  trackFeatureEvent( 'purchase_restore_failed', { store, ...failure } );
  if ( shouldCapturePurchaseFailure( error, 'restore' ) )
    crashCapture( error, purchaseCrashContext( undefined, { store, ...failure } ) );
}

function reportUnmatchedRestoredPurchase( purchase: ValidatePurchaseRequest ): void
{
  const first_report = trackFeatureEvent( 'purchase_restore_unmatched', {
    product_id: purchase.product_id,
    store: purchase.store,
    ...( purchase.transaction_id ? { transaction_id: purchase.transaction_id } : {} ),
  }, `${purchase.store}|${purchase.transaction_id ?? purchase.product_id}` );
  CrashBreadcrumbs.add( 'app', `purchase restore unmatched product=${purchase.product_id}` );
  if ( first_report )
    crashCapture( new Error( `restored_purchase_unmatched: ${purchase.product_id}` ), {
      purchase_stage: 'restore',
      productid: purchase.product_id,
      store: purchase.store,
      transaction_id: purchase.transaction_id,
    } );
}

function queueVerifiedPurchaseS2S(
  purchase: ValidatePurchaseRequest,
  validation: ValidatePurchaseResponse,
  product_id: string | undefined,
): void
{
  // The client supplies only the transaction anchor. C#Server verifies it
  // against the committed player-backend purchase before any destination
  // sees the event, so client price/currency are never optimization truth.
  if ( validation.purchase?.is_test === true ) return;
  const transaction_id = validation.purchase?.transaction_id ?? purchase.transaction_id;
  if ( transaction_id )
  {
    CustomEventsController.queueS2SEvent( 'purchase', {
      product_id: validation.purchase?.product_id ?? purchase.product_id ?? product_id,
      store: purchase.store,
    }, { transaction_id } );
  }
}

function isGooglePlayItemAlreadyOwned( error: unknown ): boolean
{
  const raw = error && typeof error === 'object' ? error as Record<string, unknown> : {};
  const code = typeof raw.code === 'string' || typeof raw.code === 'number' ? String( raw.code ) : '';
  const message = error instanceof Error ? error.message : String( error ?? '' );
  const normalized_message = message.toLowerCase();

  return code === '7' ||
    normalized_message.includes( 'item_already_owned' ) ||
    normalized_message.includes( 'item already owned' ) ||
    normalized_message.includes( 'already owned' );
}

function canReplaceOwnedGooglePlayPurchaseAfterValidationFailure( error: unknown ): boolean
{
  return error instanceof ApiError && error.status >= 400 && error.status < 500;
}

async function nativeLogTail( limit = 40 ): Promise<string[]>
{
  try
  {
    const plugin = ( window as any ).Capacitor?.Plugins?.NativeLog;
    const result = plugin?.getBufferedLogs ? await plugin.getBufferedLogs() : null;
    const logs = Array.isArray( result?.logs ) ? result.logs : [];
    return logs.slice( -limit ).map( ( line: unknown ) => typeof line === 'string' ? line : JSON.stringify( line ) );
  }
  catch
  {
    return [];
  }
}

async function consumeValidatedGooglePlayPurchase(
  purchase: Pick<ValidatePurchaseResponse['purchase'], 'store'> & { purchase_token?: string; transaction_id?: string },
): Promise<'consumed' | 'not_consumable' | 'failed'>
{
  if ( ( purchase.store !== OfferStore.GooglePlay && purchase.store !== OfferStore.AppStore ) || !purchase.purchase_token ) return 'not_consumable';

  try
  {
    const result = await IapPlugin.consumePurchase( {
      store:          purchase.store,
      purchase_token: purchase.purchase_token,
    } );
    if ( purchase.store === OfferStore.AppStore && !( result?.ok && result.consumed ) )
      crashCapture( new Error( 'appstore_finish_not_confirmed' ), {
        transaction_id: purchase.transaction_id,
        result,
        native_log: await nativeLogTail(),
      } );
    return result?.ok ? 'consumed' : 'not_consumable';
  }
  catch ( e )
  {
    console.warn( '[Offers] store finish failed after validation', e );
    const app_store = purchase.store === OfferStore.AppStore;
    CrashBreadcrumbs.add( 'app', `purchase finish failed store=${purchase.store}` );
    crashCapture( new Error( `${app_store ? 'appstore_finish_failed' : 'google_play_consume_failed'}: ${e instanceof Error ? e.message : String( e )}` ), {
      ...purchaseCrashContext( undefined, purchaseFailureProperties( e, 'store' ) ),
      purchase_stage: 'finish',
      store: purchase.store,
      transaction_id: purchase.transaction_id,
      ...( app_store ? { native_log: await nativeLogTail() } : {} ),
    } );
    return 'failed';
  }
}

function shouldConsumeGooglePlayPurchase( offer: OfferDto, server_purchase_type?: string | null ): boolean
{
  if ( server_purchase_type === 'non_consumable' || server_purchase_type === 'non-consumable' ) return false;
  if ( server_purchase_type === 'consumable' ) return true;
  // The server schema owns this flag. Keep the entitlement fallback for older
  // configs during rollout, but never consume a non-consumable product.
  const configured = readConfiguredPurchaseType( offer )?.toLowerCase();
  if ( configured === 'non_consumable' || configured === 'non-consumable' ) return false;
  return offer.entitlement_key !== NO_ADS_ENTITLEMENT;
}

function canRecoverOwnedGooglePlayPurchase( offer: OfferDto, chain_step_id?: string ): boolean
{
  const chain_step = chain_step_id ? offer.chain?.steps.find( step => step.id === chain_step_id ) : undefined;
  // A permanent chain card must be acknowledged, never consumed. A stale UI
  // can still reach this path before the refreshed eligibility hides it.
  if ( chain_step?.rewards.no_ads === true ) return false;
  return shouldConsumeGooglePlayPurchase( offer );
}

type PurchaseAnalyticsMetadata = {
  store: OfferStore;
  purchase_type: string;
  is_test: boolean;
  transaction_id?: string;
  /** Analytics pricing contract: what the player paid in USD, what the SKU was meant to cost, and the chain position. */
  priceusd?: number | null;
  originalusd?: number | null;
  revenue_usd?: number | null;
  offer_step?: number;
  price_source?: string;
};

/**
 * The USD/step properties that are known before any store call: the
 * configured price and the chain position. The paid USD amount only exists
 * once the server has confirmed the receipt.
 */
function offerPricingAnalytics( offer: OfferDto, chain_step_id?: string ): Pick<PurchaseAnalyticsMetadata, 'originalusd' | 'priceusd' | 'offer_step'>
{
  if ( offer.chain && chain_step_id )
  {
    const index = offer.chain.steps.findIndex( step => step.id === chain_step_id );
    const step = index >= 0 ? offer.chain.steps[index] : undefined;
    return {
      ...( step?.price_usd !== undefined ? { originalusd: step.price_usd, priceusd: step.price_usd } : {} ),
      ...( index >= 0 ? { offer_step: index + 1 } : {} ),
    };
  }
  return offer.price_usd !== undefined ? { originalusd: offer.price_usd, priceusd: offer.price_usd } : {};
}

/** Per-product USD price when the server attaches it to the SKU itself (legacy offers without a catalog product). */
function readStorePriceUsd( offer: OfferDto, product_override?: OfferProductDto ): number | undefined
{
  const product = product_override ?? getPlatformProductConfig( offer.products );
  const raw = product?.priceUsd;
  return typeof raw === 'number' && Number.isFinite( raw ) && raw >= 0 ? raw : undefined;
}

function readFiniteNumber( value: unknown ): number | undefined
{
  return typeof value === 'number' && Number.isFinite( value ) ? value : undefined;
}

function readConfiguredPurchaseType( offer: OfferDto ): string | null
{
  const raw = offer.data?.purchase_type ?? offer.data?.purchaseType;
  return typeof raw === 'string' && raw.trim() ? raw.trim() : null;
}

function buildPurchaseAnalyticsMetadata(
  offer: OfferDto,
  purchase: ValidatePurchaseRequest,
  validation?: ValidatePurchaseResponse | null,
  product_override?: OfferProductDto,
): PurchaseAnalyticsMetadata
{
  const validated_purchase = validation?.purchase;
  const store_price_usd = readStorePriceUsd( offer, product_override );
  const original_usd = readFiniteNumber( validated_purchase?.original_usd ) ?? readFiniteNumber( validated_purchase?.original_price_usd );
  const price_source = typeof validated_purchase?.price_source === 'string' ? validated_purchase.price_source : undefined;
  const store = validated_purchase?.store ?? purchase.store;
  const server_purchase_type = validated_purchase?.purchase_type;
  const purchase_type = typeof server_purchase_type === 'string' && server_purchase_type.trim()
    ? server_purchase_type.trim()
    : readConfiguredPurchaseType( offer )
      ?? ( shouldConsumeGooglePlayPurchase( offer ) ? 'consumable' : 'non_consumable' );
  const server_transaction_id = validated_purchase?.transaction_id;
  const transaction_id = typeof server_transaction_id === 'string' && server_transaction_id.trim()
    ? server_transaction_id.trim()
    : purchase.transaction_id;
  const is_test = !GlobalSettings.IS_PROD
    || validated_purchase?.is_test === true
    || validated_purchase?.environment === 'sandbox'
    || purchase.environment === 'sandbox'
    || store === OfferStore.Sandbox;

  return {
    store,
    purchase_type,
    is_test,
    ...( transaction_id ? { transaction_id } : {} ),
    ...offerPricingAnalytics( offer, typeof purchase.chain_step_id === 'string' ? purchase.chain_step_id : undefined ),
    ...( store_price_usd !== undefined ? { originalusd: store_price_usd } : {} ),
    ...( validated_purchase?.price_usd !== undefined ? { priceusd: validated_purchase.price_usd } : {} ),
    ...( original_usd !== undefined ? { originalusd: original_usd } : {} ),
    ...( validated_purchase?.revenue_usd !== undefined ? { revenue_usd: validated_purchase.revenue_usd } : {} ),
    ...( validated_purchase?.offer_step !== undefined ? { offer_step: validated_purchase.offer_step } : {} ),
    ...( price_source ? { price_source } : {} ),
  };
}

async function finishValidatedGooglePlayPurchase(
  offer: OfferDto,
  purchase: Pick<ValidatePurchaseResponse['purchase'], 'store' | 'purchase_type'> & { purchase_token?: string; transaction_id?: string },
): Promise<'consumed' | 'acknowledged' | 'not_required' | 'failed'>
{
  if ( ( purchase.store !== OfferStore.GooglePlay && purchase.store !== OfferStore.AppStore ) || !purchase.purchase_token ) return 'not_required';

  if ( shouldConsumeGooglePlayPurchase( offer, purchase.purchase_type ) )
  {
    const result = await consumeValidatedGooglePlayPurchase( purchase );
    return result === 'not_consumable' ? 'not_required' : result;
  }

  try
  {
    const result = await IapPlugin.acknowledgePurchase( {
      store:          purchase.store,
      purchase_token: purchase.purchase_token,
    } );
    return result?.ok ? 'acknowledged' : 'not_required';
  }
  catch ( e )
  {
    console.warn( '[Offers] Google Play acknowledge failed after validation', e );
    CrashBreadcrumbs.add( 'app', `purchase acknowledge failed store=${purchase.store}` );
    crashCapture( new Error( `google_play_acknowledge_failed: ${e instanceof Error ? e.message : String( e )}` ), {
      ...purchaseCrashContext( undefined, purchaseFailureProperties( e, 'store' ) ),
      purchase_stage: 'finish',
      store: purchase.store,
      transaction_id: purchase.transaction_id,
    } );
    return 'failed';
  }
}

clearLegacyPersistentCache();

export const OfferService = {
  async refresh(): Promise<OffersResponse | null>
  {
    if ( refresh_promise ) return refresh_promise;
    refresh_promise = ( async () =>
    {
      try
      {
        const res = await getOffersConditional( offer_session_id, offers_etag );
        if ( res.status === 304 )
          return last_offers_response;
        offers_etag = res.etag;
        last_offers_response = res.data;
        applyOffersResponse( res.data );
        return res.data;
      }
      catch ( e )
      {
        console.warn( '[Offers] refresh failed', e );
        // Manual entry points are server-authoritative. Do not keep an old
        // placement button alive when the latest availability cannot load.
        placements_cache = {};
        emitPlacementsChanged();
        return null;
      }
      finally
      {
        refresh_promise = null;
      }
    } )();

    return refresh_promise;
  },

  getOffers(): OfferDto[]
  {
    return offers_cache;
  },

  async refreshIfScheduleChanged(): Promise<OffersResponse | null>
  {
    if ( schedule_change_at_ms === null ) return null;
    const server_now_ms = Date.now() + ( server_clock_offset_ms ?? 0 );
    if ( server_now_ms < schedule_change_at_ms ) return null;
    return this.refresh();
  },

  /**
   * The backend normally supplies an absolute deadline. A temporary
   * compatibility fallback keeps a timed offer visible on older backends that
   * expose a duration only in their legacy data payload.
   */
  getSessionOfferDisplayDeadlineMs( offer: OfferDto ): number | null
  {
    return session_offer_display_deadlines_ms.get( offer.id ) ?? null;
  },

  /**
   * The deadline a timed offer counts down to. The backend spells the field
   * differently across payload shapes, and older ones expose a duration only,
   * so every screen showing a timer must resolve it the same way.
   */
  getOfferDisplayEndMs( offer: OfferDto ): number | null
  {
    const deadlines = [
      offer.session_ends_at,
      offer.first_shown_ends_at,
      offer.ends_at,
      offer.data?.session_ends_at,
      offer.data?.first_shown_ends_at,
      offer.data?.ends_at,
      offer.data?.endsAt,
    ]
      .filter( ( value ): value is string => typeof value === 'string' )
      .map( value => Date.parse( value ) )
      .filter( Number.isFinite );

    return deadlines.length ? Math.min( ...deadlines ) : this.getSessionOfferDisplayDeadlineMs( offer );
  },

  /** Returns null until the offers endpoint has supplied a server clock. */
  getServerNowMs(): number | null
  {
    return server_clock_offset_ms === null ? null : Date.now() + server_clock_offset_ms;
  },

  getOffer( offer_id: string ): OfferDto | null
  {
    return offers_cache.find( offer => offer.id === offer_id ) ?? null;
  },

  /** Current cycle of a limited offer, or null when it can be bought freely. */
  getPurchaseLimitState( offer: OfferDto ): { left: number; max: number; resets_at_ms: number | null } | null
  {
    if ( !offer.purchase_limit || offer.purchases_left === undefined ) return null;
    const resets_at_ms = offer.limit_resets_at ? Date.parse( offer.limit_resets_at ) : NaN;
    return {
      left: Math.max( 0, offer.purchases_left ),
      max: offer.purchase_limit.max,
      resets_at_ms: Number.isFinite( resets_at_ms ) ? resets_at_ms : null,
    };
  },

  /** Returns the exact server-authorized offer for a manual UI entry point. */
  getPlacementOffer( placement_id: string ): { offer: OfferDto; template: string } | null
  {
    const placement = placements_cache[placement_id];
    if ( !placement ) return null;
    const offer = this.getOffer( placement.offer_id );
    return offer?.presentation === 'placement_dialog'
      ? { offer, template: placement.template }
      : null;
  },

  /** Chained offers are a separate full-screen flow, not shop cards. */
  getChainOffers( layout: ChainLayout = 'chain_default' ): OfferDto[]
  {
    return offers_cache
      .filter( offer => offer.presentation === 'chained' && Boolean( offer.chain ) )
      .filter( offer => chainLayoutOf( offer ) === layout && this.isOfferLive( offer ) && this.hasActiveChainStep( offer ) )
      .sort( ( a, b ) => b.priority - a.priority );
  },

  /** A chain whose cursor names no step of its own has no card to act on, so its screen would open empty. */
  hasActiveChainStep( offer: OfferDto ): boolean
  {
    const chain = offer.chain;
    const active_step_id = chain?.next_step_id ?? chain?.next_paid_step_id;
    return Boolean( chain && active_step_id && chain.steps.some( step => step.id === active_step_id ) );
  },

  isOfferLive( offer: OfferDto ): boolean
  {
    const deadline_ms = this.getOfferDisplayEndMs( offer );
    if ( deadline_ms === null ) return true;
    return deadline_ms > ( this.getServerNowMs() ?? Date.now() );
  },

  getChainOfferClaimableCount( layout: ChainLayout = 'chain_default' ): number
  {
    const chain = this.getChainOffers( layout )[0]?.chain;
    const active_step_id = chain?.next_step_id ?? chain?.next_paid_step_id;
    if ( !chain || !active_step_id ) return 0;

    const step = chain.steps.find( item => item.id === active_step_id );
    return step?.free === true && step.rewarded !== true ? 1 : 0;
  },

  /**
   * Starts the store purchase for the player's current paid chain step.
   * `context` names the entry point for chains behind a placement (1+1);
   * the chain screen passes nothing and reports as before.
   */
  async purchaseChainOffer( offer_id: string, step_id: string, context?: ChainStepContext ): Promise<ValidatePurchaseResponse>
  {
    const offer = this.getOffer( offer_id );
    const chain = offer?.chain;
    if ( !offer || !chain ) throw reportOfferUnavailable( offer_id, new Error( `Chain offer not loaded: ${offer_id}` ), chainStepAnalytics( context, step_id ) );
    const setup_context = { ...chainStepAnalytics( context, step_id ), free: false };
    const active_step_id = chain.next_step_id ?? chain.next_paid_step_id;
    if ( active_step_id !== step_id )
    {
      const error = new Error( 'This chain purchase is no longer available' );
      await reportPurchaseFailure( offer, setup_context, error, 'setup', { capture: false } );
      throw error;
    }

    const step = chain.steps.find( item => item.id === step_id && item.free === false );
    const purchase = chain.purchases.find( item => item.stepId === step_id );
    const product = step?.products ? getPlatformProductConfig( step.products ) : null;
    if ( !step || !purchase || !product )
    {
      const error = new Error( `Chain step ${step_id} is not configured for this platform` );
      await reportPurchaseFailure( offer, setup_context, error, 'setup' );
      throw error;
    }

    await this.trackClicked( offer_id, { ...chainStepAnalytics( context, step_id ), free: false } );
    const result = await this.purchaseOffer( offer_id, {
      ...chainStepAnalytics( context, step_id ),
      productid: product.productId,
    }, product );
    await this.refresh();
    return result;
  },

  /** Claims the currently unlocked free card without opening a store. */
  async claimChainOfferFreeStep( offer_id: string, step_id: string, context?: ChainStepContext ): Promise<ClaimChainOfferStepResponse>
  {
    const offer = this.getOffer( offer_id );
    const chain = offer?.chain;
    if ( !offer || !chain ) throw reportOfferUnavailable( offer_id, new Error( `Chain offer not loaded: ${offer_id}` ), chainStepAnalytics( context, step_id ) );
    const step_context = { ...chainStepAnalytics( context, step_id ), free: true };
    const active_step_id = chain.next_step_id ?? chain.next_paid_step_id;
    const step = chain.steps.find( item => item.id === step_id && item.free );
    if ( active_step_id !== step_id || !step )
    {
      const error = new Error( 'This free chain reward is not ready to claim' );
      await reportPurchaseFailure( offer, step_context, error, 'setup', { capture: false } );
      throw error;
    }

    await this.trackClicked( offer_id, step_context );
    const result = await trackChainStepClaim( offer, step_context, () => claimChainOfferStep( offer_id, step_id, null, context?.placement_id ) );
    await this.refresh();
    return result;
  },

  /** Claims the current rewarded-video card after the ad SDK confirms completion. */
  async claimChainOfferRewardedStep( offer_id: string, step_id: string, rewarded_ad_id: string | null, context?: ChainStepContext ): Promise<ClaimChainOfferStepResponse>
  {
    const offer = this.getOffer( offer_id );
    const chain = offer?.chain;
    if ( !offer || !chain ) throw reportOfferUnavailable( offer_id, new Error( `Chain offer not loaded: ${offer_id}` ), chainStepAnalytics( context, step_id ) );
    const step_context = {
      ...chainStepAnalytics( context, step_id ), rewarded: true,
      ...( rewarded_ad_id ? { rewarded_ad_id } : {} ),
    };
    const active_step_id = chain.next_step_id ?? chain.next_paid_step_id;
    const step = chain.steps.find( item => item.id === step_id && item.rewarded === true );
    if ( active_step_id !== step_id || !step )
    {
      const error = new Error( 'This rewarded chain reward is not ready to claim' );
      await reportPurchaseFailure( offer, step_context, error, 'setup' );
      throw error;
    }

    await this.trackClicked( offer_id, step_context );
    const result = await trackChainStepClaim( offer, step_context, () => claimChainOfferStep( offer_id, step_id, rewarded_ad_id, context?.placement_id ) );
    await this.refresh();
    return result;
  },

  /** Trigger offers arrive from a server decision rather than GET /offers. */
  rememberOffer( offer: OfferDto ): void
  {
    offers_cache = [ ...offers_cache.filter( item => item.id !== offer.id ), offer ];
  },

  getPlatformProductId( offer_id: string ): string | null
  {
    const offer = this.getOffer( offer_id );
    return offer ? getPlatformProduct( offer ) : null;
  },

  hasEntitlement( key: string ): boolean
  {
    return entitlements_cache[key] === true
      || readPersistentEntitlements()[key] === true
      || offers_cache.some( offer => offer.entitlement_key === key && offer.owned === true );
  },

  hasNoAds(): boolean
  {
    if ( no_ads_cheat_override != null ) return no_ads_cheat_override;
    return this.hasEntitlement( NO_ADS_ENTITLEMENT );
  },

  /** Owns the permanent entitlement, or is inside a timed no_ads reward window. */
  hasNoInterstitialAds(): boolean
  {
    return this.hasNoAds() || StatisticsStorage.load().no_ads_until > Date.now();
  },

  getNoAdsCheatOverride(): boolean | null
  {
    return no_ads_cheat_override;
  },

  setNoAdsCheatOverride( no_ads: boolean | null ): void
  {
    no_ads_cheat_override = no_ads;
    // A debug override changes only ad behaviour. It must not update analytics identity.
    emitEntitlementsChanged( false );
  },

  isPurchaseSimulationAvailable(): boolean
  {
    return PURCHASE_SIMULATION_BUILD_ALLOWED && !GlobalSettings.IS_PROD;
  },

  isPurchaseSimulationEnabled(): boolean
  {
    return this.isPurchaseSimulationAvailable() && simulate_purchase_cheat_enabled;
  },

  setPurchaseSimulationEnabled( enabled: boolean ): void
  {
    // Keep this as a non-production-only guard in the service too: the debug
    // UI is not the security boundary, and a production build must never
    // substitute a real store purchase with a sandbox one.
    simulate_purchase_cheat_enabled = this.isPurchaseSimulationAvailable() && enabled;
  },

  getMonetizationSegment(): MonetizationSegment
  {
    return getMonetizationSegment();
  },

  clearCachedState(): void
  {
    offers_cache = [];
    offers_etag = null;
    last_offers_response = null;
    pending_free_claim_ids.clear();
    entitlements_cache = {};
    server_clock_offset_ms = null;
    clearLegacyPersistentCache();
    clearPersistentEntitlements();
    emitEntitlementsChanged();
  },

  async trackShown( offer_id: string, extra?: Record<string, unknown> ): Promise<void>
  {
    const offer = this.getOffer( offer_id );
    if ( offer ) await trackOfferAnalytics( offer, OfferEventType.Shown, extra );
  },

  async trackPlacementImpression( offer_id: string, extra?: Record<string, unknown> ): Promise<void>
  {
    const offer = this.getOffer( offer_id );
    if ( offer ) await trackOfferAnalytics( offer, OfferEventType.PlacementImpression, extra );
  },

  async trackClicked( offer_id: string, extra?: Record<string, unknown> ): Promise<void>
  {
    const offer = this.getOffer( offer_id );
    if ( offer ) await trackOfferAnalytics( offer, OfferEventType.Clicked, extra );
  },

  async trackClosed( offer_id: string, extra?: Record<string, unknown> ): Promise<void>
  {
    const offer = this.getOffer( offer_id );
    if ( offer ) await trackOfferAnalytics( offer, OfferEventType.Close, extra );
  },

  async trackPriceLoaded( offer_id: string, extra?: Record<string, unknown> ): Promise<void>
  {
    const offer = this.getOffer( offer_id );
    if ( offer ) await trackOfferAnalytics( offer, OfferEventType.PriceLoaded, extra );
  },

  async trackPriceUnavailable( offer_id: string, extra?: Record<string, unknown> ): Promise<void>
  {
    const offer = this.getOffer( offer_id );
    if ( offer ) await trackOfferAnalytics( offer, OfferEventType.PriceUnavailable, extra );
  },

  /**
   * A rewarded offer's ad is the first purchase stage: an ad that was not
   * watched to the end is a failed purchase with a reason, the same shape the
   * store stage reports, so the funnel reads continuously.
   */
  async trackRewardedAdOutcome( offer_id: string, watched: RewardedShowResult, extra?: Record<string, unknown> ): Promise<void>
  {
    const offer = this.getOffer( offer_id );
    if ( !offer ) return;
    const ad_context = { rewarded: true, ...( watched.ad_id ? { rewarded_ad_id: watched.ad_id } : {} ), ...extra };
    if ( watched.rewarded )
    {
      await trackOfferAnalytics( offer, OfferEventType.Clicked, { ...ad_context, purchase_stage: 'rewarded_ad', rewarded_ad_completed: true } );
      return;
    }
    await trackOfferAnalytics( offer, OfferEventType.PurchaseFailed, {
      ...ad_context,
      purchase_stage: 'rewarded_ad',
      failure_reason: watched.refused ? 'rewarded_ad_refused' : watched.shown ? 'rewarded_ad_not_rewarded' : 'rewarded_ad_unavailable',
    } );
  },

  isFreeOffer( offer_id: string ): boolean
  {
    const offer = this.getOffer( offer_id );
    return Boolean( offer && isFreeOffer( offer ) );
  },

  /** Claims a server-authorized free static or trigger offer. This deliberately
   * bypasses IAP: the server validates the durable per-player claim limit. */
  /** A rewarded offer is claimed through the same call once the ad SDK confirmed the video. */
  isRewardedOffer( offer_id: string ): boolean
  {
    const offer = this.getOffer( offer_id );
    return Boolean( offer && isRewardedOffer( offer ) );
  },

  async claimFreeOffer(
    offer_id: string,
    analytics_context?: Record<string, unknown>,
    rewarded?: { ad_id: string | null },
  ): Promise<ClaimFreeOfferResponse>
  {
    const offer = this.getOffer( offer_id );
    if ( !offer ) throw reportOfferUnavailable( offer_id, new Error( `Free offer not loaded: ${offer_id}` ), analytics_context );
    if ( !( isFreeOffer( offer ) || ( isRewardedOffer( offer ) && rewarded ) ) )
    {
      const error = new Error( `Offer ${offer_id} is not claimable without a store purchase` );
      await reportPurchaseFailure( offer, { free: true, ...( rewarded ? { rewarded: true } : {} ), ...analytics_context }, error, 'setup' );
      throw error;
    }
    const claim_id = pending_free_claim_ids.get( offer_id ) ?? ( typeof globalThis.crypto?.randomUUID === 'function'
      ? globalThis.crypto.randomUUID()
      : `free-claim-${Date.now()}-${Math.random().toString( 36 ).slice( 2 )}` );
    pending_free_claim_ids.set( offer_id, claim_id );
    const trigger_event_id = typeof analytics_context?.trigger_event_id === 'string'
      ? analytics_context.trigger_event_id
      : undefined;
    const placement_id = typeof analytics_context?.placement_id === 'string'
      ? analytics_context.placement_id
      : undefined;
    const context = { free: true, claim_id, priceusd: 0, revenue_usd: 0, ...offerPricingAnalytics( offer ), ...( rewarded ? { rewarded: true, rewarded_ad_id: rewarded.ad_id } : {} ), ...analytics_context };
    await trackOfferAnalytics( offer, OfferEventType.PurchaseStarted, context );
    try
    {
      const result = await claimFreeOffer( offer_id, claim_id, trigger_event_id, offer_session_id, placement_id, rewarded );
      pending_free_claim_ids.delete( offer_id );
      applyPurchaseLimitResult( offer_id, result );
      await trackOfferAnalytics( offer, OfferEventType.FreeClaim, {
        ...context,
        grant_applied: result.grant_applied,
        claim_count: result.claim_count,
        ...grantedAnalytics( result.granted ),
        ...purchaseLimitAnalytics( result ),
      } );
      if ( result.grant_applied !== false ) logRewardCoins( result.granted, rewardSource( offer, 'free' ) );
      await this.refresh();
      return result;
    }
    catch ( error )
    {
      await reportPurchaseFailure( offer, context, error, 'claim' );
      throw error;
    }
  },

  async purchaseOffer(
    offer_id: string,
    analytics_context?: Record<string, unknown>,
    product_override?: OfferProductDto,
  ): Promise<ValidatePurchaseResponse>
  {
    const offer = this.getOffer( offer_id );
    if ( !offer ) throw reportOfferUnavailable( offer_id, new Error( `Offer not loaded: ${offer_id}` ), analytics_context );
    if ( isFreeOffer( offer ) || isRewardedOffer( offer ) )
    {
      const error = new Error( 'Free and rewarded offers must be claimed without a store purchase' );
      await reportPurchaseFailure( offer, { ...analytics_context }, error, 'setup' );
      throw error;
    }

    const product_id = product_override?.productId ?? getPlatformProduct( offer );
    if ( !product_id )
    {
      const error = new Error( `Offer ${offer_id} is not configured for this platform` );
      await reportPurchaseFailure( offer, { ...analytics_context }, error, 'setup' );
      throw error;
    }

    const started_at = Date.now();
    const elapsed = (): { purchase_duration_ms: number } => ( { purchase_duration_ms: Date.now() - started_at } );
    const is_simulated_purchase = this.isPurchaseSimulationEnabled();
    const store_price_usd = readStorePriceUsd( offer, product_override );
    const common_analytics_context = {
      productid: product_id,
      ...( store_price_usd !== undefined ? { originalusd: store_price_usd } : {} ),
      ...( is_simulated_purchase ? { test_purchase: true } : {} ),
      ...analytics_context,
    };
    const chain_step_id = typeof analytics_context?.chain_step_id === 'string' ? analytics_context.chain_step_id : undefined;
    const trigger_event_id = typeof analytics_context?.trigger_event_id === 'string' ? analytics_context.trigger_event_id : undefined;
    const placement_id = typeof analytics_context?.placement_id === 'string' ? analytics_context.placement_id : undefined;
    Object.assign( common_analytics_context, offerPricingAnalytics( offer, chain_step_id ) );
    await trackOfferAnalytics( offer, OfferEventType.PurchaseStarted, common_analytics_context );
    CrashBreadcrumbs.add( 'app', `purchase start offer=${offer.id} product=${product_id}${is_simulated_purchase ? ' simulated' : ''}` );

    let purchase: ValidatePurchaseRequest | undefined = is_simulated_purchase
      ? createSimulatedPurchase( product_id )
      : undefined;
    let validation: ValidatePurchaseResponse | null = null;
    let recovered_owned_product = false;
    let reused_owned_purchase = false;
    let owned_transaction_id: string | undefined;
    if ( !purchase ) try
    {
      purchase = await IapPlugin.purchaseProduct( product_id );
    }
    catch ( e )
    {
      if ( isGooglePlayItemAlreadyOwned( e ) && IapPlugin.getStore() === OfferStore.GooglePlay )
      {
        try
        {
          const restored = await IapPlugin.restorePurchases();
          const owned_purchase = restored.find( item => item.product_id === product_id );

          if ( owned_purchase )
          {
            owned_transaction_id = owned_purchase.transaction_id;
            try
            {
              validation = await validateOfferPurchase( offer.id, withOfferPurchaseMetadata( offer, owned_purchase, product_override, chain_step_id, trigger_event_id, placement_id ) );
              purchase = owned_purchase;
              reused_owned_purchase = true;
            }
            catch ( validation_error )
            {
              markPurchaseStage( validation_error, 'validation' );
              if ( !canReplaceOwnedGooglePlayPurchaseAfterValidationFailure( validation_error ) || !canRecoverOwnedGooglePlayPurchase( offer, chain_step_id ) ) throw validation_error;

              const consume_result = await IapPlugin.consumeOwnedProduct( product_id );
              if ( !consume_result?.ok ) throw validation_error;

              recovered_owned_product = consume_result.consumed;
              purchase = await IapPlugin.purchaseProduct( product_id );
            }
          }
          else
          {
            if ( !canRecoverOwnedGooglePlayPurchase( offer, chain_step_id ) ) throw e;
            const consume_result = await IapPlugin.consumeOwnedProduct( product_id );
            if ( !consume_result?.ok ) throw e;

            recovered_owned_product = consume_result.consumed;
            purchase = await IapPlugin.purchaseProduct( product_id );
          }
        }
        catch ( retry_error )
        {
          const stage = purchaseStageOf( retry_error ) === 'validation' ? 'validation' : 'store';
          await reportPurchaseFailure( offer, common_analytics_context, retry_error, stage, {
            extra: {
              ...elapsed(),
              recovered_owned_product,
              initial_failure_reason: 'item_already_owned',
              ...( stage === 'validation' ? { store: OfferStore.GooglePlay } : {} ),
              ...( stage === 'validation' && owned_transaction_id ? { transaction_id: owned_transaction_id } : {} ),
            },
          } );
          throw retry_error;
        }
      }
      else
      {
        await reportPurchaseFailure( offer, common_analytics_context, e, 'store', { extra: elapsed() } );
        throw e;
      }
    }

    if ( !purchase )
    {
      const error = new Error( 'purchase_result_missing' );
      await reportPurchaseFailure( offer, common_analytics_context, error, 'store', {
        extra: { ...elapsed(), error_message: 'Purchase result missing after Google Play owned-product recovery' },
      } );
      throw error;
    }

    const paid_purchase = purchase;
    CrashBreadcrumbs.add( 'app', `purchase paid offer=${offer.id} store=${paid_purchase.store}${reused_owned_purchase ? ' reused' : ''}` );
    let validated_purchase: ValidatePurchaseResponse;
    try
    {
      validated_purchase = validation ?? await validateOfferPurchase( offer.id, withOfferPurchaseMetadata( offer, paid_purchase, product_override, chain_step_id, trigger_event_id, placement_id ) );
    }
    catch ( e )
    {
      await reportPurchaseFailure( offer, { ...common_analytics_context, ...buildOfferPriceAnalyticsProperties( paid_purchase ) }, e, 'validation', {
        extra: {
          ...elapsed(),
          store: paid_purchase.store,
          ...( paid_purchase.transaction_id ? { transaction_id: paid_purchase.transaction_id } : {} ),
        },
        crash_extra: paid_purchase.store === OfferStore.AppStore ? { native_log: await nativeLogTail() } : undefined,
      } );
      throw e;
    }

    let success_tracked = false;
    try
    {
      applyPurchaseLimitResult( offer.id, validated_purchase );
      const purchase_analytics_metadata = buildPurchaseAnalyticsMetadata( offer, paid_purchase, validated_purchase, product_override );
      const consume_status = await finishValidatedGooglePlayPurchase( offer, {
        ...paid_purchase,
        purchase_type: validated_purchase.purchase?.purchase_type,
      } );
      const completed_purchase_context = {
        ...common_analytics_context,
        ...buildOfferPriceAnalyticsProperties( paid_purchase ),
        ...purchase_analytics_metadata,
        ...purchaseLimitAnalytics( validated_purchase ),
      };
      const entitlement = validated_purchase.entitlement;
      if ( entitlement?.active )
      {
        persistEntitlement( entitlement.key );
        entitlements_cache[entitlement.key] = true;
        offers_cache = offers_cache.map( item =>
          item.entitlement_key === entitlement.key ? { ...item, owned: true } : item
        );
        emitEntitlementsChanged();
      }

      if ( validated_purchase.grant_applied !== false )
        queueVerifiedPurchaseS2S( paid_purchase, validated_purchase, paid_purchase.product_id ?? product_id );

      if ( validated_purchase.grant_applied !== false && offer.presentation !== 'static_shop' )
        logRewardCoins( validated_purchase.granted, rewardSource( offer, 'purchase' ) );
      await trackOfferAnalytics( offer, OfferEventType.PurchaseSuccess, {
        ...completed_purchase_context,
        ...grantedAnalytics( validated_purchase.granted ),
        ...elapsed(),
        purchaseid: validated_purchase.purchase?.id,
        purchase_status: validated_purchase.purchase?.status,
        entitlement_active: validated_purchase.entitlement?.active,
        google_play_consume_status: consume_status,
        recovered_owned_product,
        reused_owned_purchase,
      } );
      success_tracked = true;
      CrashBreadcrumbs.add( 'app', `purchase success offer=${offer.id} finish=${consume_status}` );
      // The preceding validation is authoritative; only then may a purchase
      // based trigger be resolved. Dynamic import avoids a UI/service cycle.
      void import( './offer_trigger_controller.js' ).then( ({ OfferTriggerController }) =>
        OfferTriggerController.report( 'purchase_completed', { context: { offer_id: offer.id } } )
      );
    }
    catch ( post_error )
    {
      const transaction_id = validated_purchase.purchase?.transaction_id ?? paid_purchase.transaction_id;
      CrashBreadcrumbs.add( 'app', `purchase post-validation error offer=${offer.id}` );
      crashCapture( post_error, {
        offer_id: offer.id,
        productid: product_id,
        purchase_stage: 'post_validation',
        store: paid_purchase.store,
        transaction_id,
        success_tracked,
      } );
      if ( !success_tracked )
        await trackOfferAnalytics( offer, OfferEventType.PurchaseSuccess, {
          ...common_analytics_context,
          ...elapsed(),
          purchaseid: validated_purchase.purchase?.id,
          purchase_status: validated_purchase.purchase?.status,
          entitlement_active: validated_purchase.entitlement?.active,
          is_test: validated_purchase.purchase?.is_test,
          transaction_id,
          post_validation_error: ( post_error instanceof Error ? post_error.message : String( post_error ) ).slice( 0, 300 ),
        } ).catch( () => undefined );
    }

    return validated_purchase;
  },

  async restorePurchases(): Promise<void>
  {
    let restored = [];
    try
    {
      restored = await IapPlugin.restorePurchases();
    }
    catch ( e )
    {
      console.warn( '[Offers] restore failed', e );
      reportRestoreQueryFailure( e );
      return;
    }

    if ( !restored.length ) return;
    CrashBreadcrumbs.add( 'app', `purchase restore found=${restored.length}` );

    for ( const purchase of restored )
    {
      const match = findRestoredOfferMatch( purchase.product_id );
      if ( !match )
      {
        reportUnmatchedRestoredPurchase( purchase );
        continue;
      }

      let validation: ValidatePurchaseResponse;
      try
      {
        validation = await validateOfferPurchase( match.offer.id, withOfferPurchaseMetadata(
          match.offer, purchase, match.product, match.chain_step_id, undefined, match.offer.placement?.id
        ) );
      }
      catch ( e )
      {
        console.warn( '[Offers] restore validation failed', e );
        const finished_duplicate = purchase.store === OfferStore.AppStore && e instanceof ApiError && e.status === 409;
        await reportPurchaseFailure( match.offer, {
          productid: purchase.product_id,
          ...( match.chain_step_id ? { chain_step_id: match.chain_step_id } : {} ),
          ...( match.offer.placement?.id ? { placement_id: match.offer.placement.id } : {} ),
          restored: true,
        }, e, 'validation', {
          capture: !finished_duplicate && !isPurchaseNetworkError( e ),
          extra: {
            store: purchase.store,
            ...( purchase.transaction_id ? { transaction_id: purchase.transaction_id } : {} ),
          },
        } ).catch( () => undefined );
        if ( finished_duplicate )
        {
          console.warn( `[Offers] finishing App Store transaction ${purchase.transaction_id ?? '?'} the server already rejected as a duplicate` );
          try { await IapPlugin.consumePurchase( purchase ); }
          catch ( finish_error ) { console.warn( '[Offers] could not finish duplicate App Store transaction', finish_error ); }
        }
        continue;
      }

      try
      {
        const entitlement = validation.entitlement;
        await finishValidatedGooglePlayPurchase( match.offer, {
          ...purchase,
          purchase_type: validation.purchase?.purchase_type,
        } );
        if ( validation.grant_applied !== false )
        {
          queueVerifiedPurchaseS2S( purchase, validation, purchase.product_id );
          await trackOfferAnalytics( match.offer, OfferEventType.PurchaseSuccess, {
            productid: purchase.product_id,
            ...( match.chain_step_id ? { chain_step_id: match.chain_step_id } : {} ),
            ...( match.offer.placement?.id ? { placement_id: match.offer.placement.id } : {} ),
            ...buildOfferPriceAnalyticsProperties( {
              ...purchase,
              currency: purchase.currency ?? validation.purchase?.currency ?? undefined,
              price_micros: purchase.price_micros ?? validation.purchase?.price_micros ?? undefined,
            } ),
            ...buildPurchaseAnalyticsMetadata( match.offer, purchase, validation, match.product ),
            ...grantedAnalytics( validation.granted ),
            purchaseid: validation.purchase?.id,
            purchase_status: validation.purchase?.status,
            entitlement_active: validation.entitlement?.active,
            restored: true,
          } );
          CrashBreadcrumbs.add( 'app', `purchase restore credited offer=${match.offer.id}` );
        }
        if ( entitlement?.active )
        {
          persistEntitlement( entitlement.key );
          entitlements_cache[entitlement.key] = true;
          offers_cache = offers_cache.map( item =>
            item.entitlement_key === entitlement.key ? { ...item, owned: true } : item
          );
        }
      }
      catch ( post_error )
      {
        console.warn( '[Offers] restore finish failed', post_error );
        crashCapture( post_error, {
          offer_id: match.offer.id,
          productid: purchase.product_id,
          purchase_stage: 'post_validation',
          restored: true,
          store: purchase.store,
          transaction_id: validation.purchase?.transaction_id ?? purchase.transaction_id,
        } );
      }
    }

    emitEntitlementsChanged();
  },
};
